import { useState } from 'react'
import type { AccountRole, CustomerAccountListItem } from '@/entities/customer-account'
import { Button } from '@/shared/ui/button'
import { Dialog } from '@/shared/ui/dialog'
import { Field, Input } from '@/shared/ui/field'
import { Select } from '@/shared/ui/select'
import { toast } from '@/shared/ui/toast'
import { useCreateAccount, useEditAccount } from '../api/manage'

interface AccountDialogProps {
  companyId: string
  account?: CustomerAccountListItem
  open: boolean
  onClose: () => void
}

// Создание/правка человека компании. При правке логин неизменяем, пароль —
// опционально (пусто — не менять).
export function AccountDialog({ companyId, account, open, onClose }: AccountDialogProps) {
  const isEdit = account != null
  const create = useCreateAccount(companyId)
  const edit = useEditAccount(account?.id ?? '', companyId)
  const [fullName, setFullName] = useState(account?.full_name ?? '')
  const [login, setLogin] = useState(account?.login ?? '')
  const [password, setPassword] = useState('')
  const [jobTitle, setJobTitle] = useState(account?.job_title ?? '')
  const [role, setRole] = useState<AccountRole>(account?.role ?? 'member')
  const [error, setError] = useState('')
  const busy = create.isPending || edit.isPending

  function submit() {
    if (!fullName.trim()) return setError('Укажите имя')
    if (!isEdit && !login.trim()) return setError('Укажите логин')
    if (!isEdit && !password) return setError('Задайте пароль')
    setError('')
    const done = {
      onSuccess: () => {
        toast.success(isEdit ? 'Сохранено' : 'Человек добавлен')
        onClose()
      },
      onError: (e: Error) => toast.error(e.message || 'Не удалось сохранить'),
    }
    if (isEdit) {
      edit.mutate(
        {
          full_name: fullName.trim(),
          role,
          ...(jobTitle.trim() ? { job_title: jobTitle.trim() } : { job_title: '' }),
          ...(password ? { password } : {}),
        },
        done,
      )
    } else {
      create.mutate(
        {
          full_name: fullName.trim(),
          login: login.trim(),
          password,
          role,
          ...(jobTitle.trim() ? { job_title: jobTitle.trim() } : {}),
        },
        done,
      )
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isEdit ? 'Человек компании' : 'Новый человек'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Отмена
          </Button>
          <Button onClick={submit} disabled={busy}>
            {busy ? 'Сохраняем…' : isEdit ? 'Сохранить' : 'Добавить'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Имя" {...(error && !fullName.trim() ? { error } : {})}>
          {(props) => (
            <Input {...props} value={fullName} onChange={(e) => setFullName(e.target.value)} />
          )}
        </Field>
        <Field
          label="Логин"
          {...(error && !isEdit && !login.trim() ? { error } : {})}
          {...(isEdit ? { hint: 'Логин изменить нельзя' } : {})}
        >
          {(props) => (
            <Input
              {...props}
              value={login}
              disabled={isEdit}
              onChange={(e) => setLogin(e.target.value)}
              placeholder="ivan@company.ru"
            />
          )}
        </Field>
        <Field
          label={isEdit ? 'Новый пароль' : 'Пароль'}
          {...(error && !isEdit && !password ? { error } : {})}
          {...(isEdit ? { hint: 'Оставьте пустым, чтобы не менять' } : {})}
        >
          {(props) => (
            <Input
              {...props}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
            />
          )}
        </Field>
        <Field label="Должность">
          {(props) => (
            <Input {...props} value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} />
          )}
        </Field>
        <Field label="Роль">
          {(props) => (
            <Select
              {...props}
              value={role}
              onChange={(v) => setRole(v as AccountRole)}
              options={[
                { value: 'member', label: 'Участник' },
                { value: 'admin', label: 'Администратор' },
              ]}
            />
          )}
        </Field>
      </div>
    </Dialog>
  )
}
